import { EpicRefined } from 'interfaces/middleware'
import { of, merge } from 'rxjs'
import { mergeMap, map } from 'rxjs/operators'
import {
  CCOnboardingActions,
  CreateCCAutoPaymentAction,
  CreateCCPaymentAction,
  DeleteCCAutoPaymentAction,
  DeleteCCPaymentAction,
  GetCCPaymentsAction,
  GetCCUpcomingPaymentsAction,
} from 'components/features/creditCard/creditCardStore/actions'
import {
  CC_PAYMENT_CREATE_URL,
  CC_PAYMENT_DELETE_URL,
  CC_PAYMENTS_URL,
  CC_UPCOMING_PAYMENTS_URL,
  CC_AUTO_PAYMENT_DELETE_URL,
  CC_AUTO_PAYMENT_CREATE_URL,
} from 'components/features/creditCard/creditCardStore/endpoints'
import { ajaxCatch, AjaxCatchExtraActions } from 'rxOperators/ajaxCatch'
import { filterRequest } from 'rxOperators/filterRequestOfType'
import FloatingPaneAction, { ModifyFloatingPaneAction } from 'actions/floatingPanesActions'
import { CREDIT_CARD_PANE } from 'components/features/creditCard/creditCardStore/floatingPane'
import { normalize } from 'helpers/normalizeHelpers'
import { CCCreatePaymentApiRequest } from 'components/features/creditCard/creditCardStore/types'

export const getCCPaymentsEpic: EpicRefined<CCOnboardingActions | AjaxCatchExtraActions> = (action$, _store, { get }) => action$.pipe(
  filterRequest(GetCCPaymentsAction.type),
  mergeMap((action) => get(CC_PAYMENTS_URL).pipe(
    map(({ response }) => {
      const { results } = response
      return action.success(
        normalize(results, 'uid')
      )
    }),
    ajaxCatch((error) => of(action.fail({ errorCode: error.status }))),
  )),
)

export const getCCUpcomingPaymentsEpic: EpicRefined<CCOnboardingActions | AjaxCatchExtraActions> = (action$, _store, { get }) => action$.pipe(
  filterRequest(GetCCUpcomingPaymentsAction.type),
  mergeMap((action) => get(CC_UPCOMING_PAYMENTS_URL).pipe(
    map(({ response }) => {
      const { items } = response

      return action.success(
        normalize(items, 'uid')
      )
    }),
    ajaxCatch((error) => of(action.fail({ errorCode: error.status }))),
  )),
)

export const createCCPaymentEpic: EpicRefined<CCOnboardingActions | AjaxCatchExtraActions | FloatingPaneAction> = (action$, _, { post }) => action$.pipe(
  filterRequest(CreateCCPaymentAction.type),
  mergeMap((action) => {
    const params: CCCreatePaymentApiRequest = {
      ...action.payload.params,
      payment_amount_type: 'fixed', // All one-time payments should be fixed as far as the backend is concerned
    }

    return post(CC_PAYMENT_CREATE_URL, { body: params }).pipe(
      mergeMap(({ response }) => {
        return merge(
          of(new ModifyFloatingPaneAction({
            type: CREDIT_CARD_PANE,
            mode: 'payments',
            step: 'success',
            backButton: null,
          })),
          of(action.success(response)),
          of(new GetCCUpcomingPaymentsAction())
        )
      }),
      ajaxCatch((error) => of(action.fail({ errorCode: error.status, errorsMap: error.response })))
    )
  }),
)

export const deleteCCPaymentEpic: EpicRefined<CCOnboardingActions | AjaxCatchExtraActions | FloatingPaneAction> = (action$, _, { post }) => action$.pipe(
  filterRequest(DeleteCCPaymentAction.type),
  mergeMap((action) => {

    return post({ url: CC_PAYMENT_DELETE_URL, params: { uid: action.payload.params }}, {}).pipe(
      mergeMap(({ response }) => {
        return merge(
          of(new ModifyFloatingPaneAction({
            type: CREDIT_CARD_PANE,
            mode: 'payments',
          })),
          of(action.success(response)),
          of(new GetCCPaymentsAction()),
          of(new GetCCUpcomingPaymentsAction()),
        )
      }),
      ajaxCatch((error) => of(action.fail({ errorCode: error.status, errorsMap: error.response })))
    )
  }),
)

export const createCCAutoPaymentEpic: EpicRefined<CCOnboardingActions | AjaxCatchExtraActions | FloatingPaneAction> = (action$, _, { post }) => action$.pipe(
  filterRequest(CreateCCAutoPaymentAction.type),
  mergeMap((action) => {
    return post(CC_AUTO_PAYMENT_CREATE_URL, { body: action.payload.params }).pipe(
      mergeMap(({ response }) => {
        return merge(
          of(new ModifyFloatingPaneAction({
            type: CREDIT_CARD_PANE,
            mode: 'payments',
            step: 'success',
            backButton: null,
          })),
          of(action.success(response)),
          of(new GetCCPaymentsAction()),
          of(new GetCCUpcomingPaymentsAction()),
        )
      }),
      ajaxCatch((error) => of(action.fail({ errorCode: error.status, errorsMap: error.response })))
    )
  }),
)

export const deleteCCAutoPaymentEpic: EpicRefined<CCOnboardingActions | AjaxCatchExtraActions | FloatingPaneAction> = (action$, _, { post }) => action$.pipe(
  filterRequest(DeleteCCAutoPaymentAction.type),
  mergeMap((action) => {
    return post(CC_AUTO_PAYMENT_DELETE_URL).pipe(
      mergeMap(() => {
        return merge(
          of(new ModifyFloatingPaneAction({
            type: CREDIT_CARD_PANE,
            mode: 'payments',
          })),
          of(action.success()),
          of(new GetCCPaymentsAction()),
          of(new GetCCUpcomingPaymentsAction()),
        )
      }),
      ajaxCatch((error) => of(action.fail({ errorCode: error.status, errorsMap: error.response })))
    )
  }),
)
